// @flow
import {createSelector} from "reselect";
import isEmpty from "lodash/isEmpty";
import type {State} from "../../Store";
import CurrentProposalSelectors from "../CurrentProposal/selectors";
import {RideStatus} from "../../../Utils/AppUtils";
import type {CurrentRide} from "./reducers";
import {getDistance, getPositionAsDecimalFromLatLngLiteral} from "../../../Utils/MapUtils";
import AppSelectors from "../App/selectors";

const currentRideSelector = (state: State): CurrentRide => state.currentRide;

const isConcreteRide = createSelector(
    currentRideSelector,
    (currentRide) => {
        return currentRide && !isEmpty(currentRide);
    }
);

const getRidePickup = createSelector(
    currentRideSelector,
    (currentRide) => {
        return currentRide.pickup_location;
    }
);

const getRideDropoff = createSelector(
    currentRideSelector,
    (currentRide) => {
        return currentRide.dropoff_location;
    }
);

const getPickup = createSelector(
    CurrentProposalSelectors.getProposalPickup,
    getRidePickup,
    (proposalPickup, ridePickup) => {
        if (ridePickup && !isEmpty(ridePickup)) {
            return ridePickup;
        }
        return proposalPickup;
    }
);

const getPickupLat = createSelector(
    getPickup,
    (pickup) => {
        return pickup && pickup.lat;
    }
);

const getPickupLng = createSelector(
    getPickup,
    (pickup) => {
        return pickup && pickup.lng;
    }
);

const getDropoff = createSelector(
    CurrentProposalSelectors.getProposalDropoff,
    getRideDropoff,
    (proposalDropoff, rideDropoff) => {
        if (rideDropoff && !isEmpty(rideDropoff)) {
            return rideDropoff;
        }
        return proposalDropoff;
    }
);

const getDropoffLat = createSelector(
    getDropoff,
    (dropoff) => {
        return dropoff.lat;
    }
);

const getDropoffLng = createSelector(
    getDropoff,
    (dropoff) => {
        return dropoff.lng;
    }
);

const getPickupAddress = createSelector(
    getPickup,
    (pickup) => {
        return pickup.address;
    }
);

const getRideStatus = createSelector(
    currentRideSelector,
    (currentRide) => {
        if (!currentRide || isEmpty(currentRide)) {
            return -1;
        }
        return RideStatus[currentRide.ride_status] || -1;
    }
);

const isRideAccepted = createSelector(
    getRideStatus,
    (rideStatus) => {
        return rideStatus === RideStatus.ACCEPTED;
    }
);

const isRideBoarded = createSelector(
    getRideStatus,
    (rideStatus) => {
        return rideStatus === RideStatus.BOARDED;
    }
);

const isRideCancelled = createSelector(
    getRideStatus,
    (rideStatus) => {
        return rideStatus === RideStatus.CANCELLED;
    }
);

const isRideNoShow = createSelector(
    getRideStatus,
    (rideStatus) => {
        return rideStatus === RideStatus.NO_SHOW;
    }
);

const isRideFinished = createSelector(
    getRideStatus,
    (rideStatus) => {
        return rideStatus === RideStatus.FINISHED;
    }
);

const isRideFeedback = createSelector(
    getRideStatus,
    (rideStatus) => {
        return rideStatus === RideStatus.FEEDBACK;
    }
);

const getVehicle = createSelector(
    currentRideSelector,
    (currentRide) => {
        const vehicle_details = currentRide && currentRide.vehicle_details;
        if (!vehicle_details || isEmpty(vehicle_details)) {
            return {};
        }
        return {
            identifier: vehicle_details.identifier,
            plate: vehicle_details.number,
        };
    }
);

const getVehicleIdentifier = createSelector(
    getVehicle,
    (vehicle) => {
        return vehicle.identifier;
    }
);
const getVehiclePlate = createSelector(
    getVehicle,
    (vehicle) => {
        return vehicle.plate;
    }
);

const getDriver = createSelector(
    currentRideSelector,
    (currentRide) => {
        const vehicle_details = currentRide && currentRide.vehicle_details;
        if (!vehicle_details || isEmpty(vehicle_details)) {
            return {};
        }
        return {
            phone_number: vehicle_details.driver_phone_number,
            name: vehicle_details.driver_name,
        };
    }
);

const getDriverName = createSelector(
    getDriver,
    (driver) => {
        return driver && driver.name;
    }
);

const getETADescription = createSelector(
    currentRideSelector,
    (currentRide) => {
        return currentRide && currentRide.eta_description;
    }
);

const getWalkingMap = createSelector(
    currentRideSelector,
    (currentRide) => {
        return currentRide && currentRide.walkingMap;
    }
);

const isPendingWalkingMap = createSelector(
    getWalkingMap,
    (walkingMap) => {
        return walkingMap && walkingMap.pending;
    }
);

const getWalkingMapGeometry = createSelector(
    getWalkingMap,
    isPendingWalkingMap,
    (walkingMap, isPending) => {
        if (isPending) {
            return;
        }
        if (!walkingMap || isEmpty(walkingMap)) {
            return;
        }
        if (walkingMap.overview_polyline) {
            return window.google.maps.geometry.encoding.decodePath(walkingMap.overview_polyline.points);
        } else {
            return walkingMap.legsLatLngs;
        }
    }
);

const getVanLocation = createSelector(
    currentRideSelector,

    (currentRide) => {
        return currentRide.vehicle_location ? currentRide.vehicle_location : {lat: 0, lng: 0, bearing: 0};
    },
);
const getVanLocationLat = createSelector(
    getVanLocation,
    (vanLocation) => {
        return vanLocation.lat;
    },
);
const getVanLocationLng = createSelector(
    getVanLocation,
    (vanLocation) => {
        return vanLocation.lng;
    },
);
const getVanLocationBearing = createSelector(
    getVanLocation,
    (vanLocation) => {
        return vanLocation.bearing;
    },
);

const isAwaitingDriverApproval = createSelector(
    currentRideSelector,
    (currentRide) => {
        return !!(currentRide && currentRide.awaitingDriverApproval);
    },
);

const getDistanceFromRiderToPickup = createSelector(
    AppSelectors.getRiderLocation,
    getPickup,
    (riderLocation, pickup) => {
        if (!riderLocation || isEmpty(riderLocation) || !pickup || isEmpty(pickup)) {
            return -1;
        }
        return getDistance(
            getPositionAsDecimalFromLatLngLiteral(pickup),
            getPositionAsDecimalFromLatLngLiteral(riderLocation));
    },
);

const getDriverPhone = createSelector(
    currentRideSelector,
    (currentRide) => {
        return currentRide && currentRide.driver_phone;
    },
);

export default {
    currentRideSelector,
    isConcreteRide,
    getRidePickup,
    getRideDropoff,
    getPickup,
    getPickupLat,
    getPickupLng,
    getDropoff,
    getDropoffLat,
    getDropoffLng,
    getPickupAddress,
    getRideStatus,
    isRideAccepted,
    isRideBoarded,
    isRideCancelled,
    isRideNoShow,
    isRideFinished,
    isRideFeedback,
    getVehicle,
    getVehicleIdentifier,
    getVehiclePlate,
    getDriver,
    getDriverName,
    getETADescription,
    isPendingWalkingMap,
    getWalkingMapGeometry,
    getVanLocation,
    getVanLocationLat,
    getVanLocationLng,
    getVanLocationBearing,
    isAwaitingDriverApproval,
    getDistanceFromRiderToPickup,
    getDriverPhone,
};


// WEBPACK FOOTER //
// ./src/Store/Ducks/CurrentRide/selectors.js